import React, { useState, useRef, useEffect } from 'react';
import { useToken } from '../App';
import { useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

interface GuestOtpProps {
  onSuccess: (mobile: string, guestId: string) => void;
}

const OTP_LENGTH = 6;
const RESEND_WAIT_SECONDS = 60;

const GuestOtp: React.FC<GuestOtpProps> = ({ onSuccess }) => {
  // Always call hooks unconditionally
  const tokenCtx = useToken();
  const params = useParams<{ hotelId?: string; roomId?: string }>();
  const hotelId = tokenCtx?.hotelId || params.hotelId || '';
  const roomId = tokenCtx?.roomId || params.roomId || '';
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hotelName, setHotelName] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [checkedInGuestName, setCheckedInGuestName] = useState('');
  const [isRoomOccupied, setIsRoomOccupied] = useState<boolean | null>(null);
  const [hotelLoading, setHotelLoading] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const otpContainerRef = useRef<HTMLDivElement | null>(null);

  // Auto-focus first OTP field on step change
  useEffect(() => {
    if (step === 'verify') {
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  // Remove shake after animation
  useEffect(() => {
    if (shake) {
      const timer = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [shake]);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (step !== 'verify' || resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [step, resendCooldown]);

  // Fetch hotel name
  useEffect(() => {
    if (hotelId) {
      setHotelLoading(true);
      fetch(API_ENDPOINTS.HOTELS(hotelId))
        .then(res => res.json())
        .then(data => {
          setHotelName(data.hotel_name || '');
          setHotelLoading(false);
        })
        .catch(() => {
          setHotelName('');
          setHotelLoading(false);
        });
    }
  }, [hotelId]);

  // Fetch room number for display on login screen
  useEffect(() => {
    if (hotelId && roomId) {
      setIsRoomOccupied(null);
      fetch(API_ENDPOINTS.ROOM_GUEST(hotelId, roomId))
        .then(res => {
          if (!res.ok) {
            setRoomNumber(roomId);
            setCheckedInGuestName('');
            setIsRoomOccupied(false);
            return null;
          }
          return res.json();
        })
        .then(data => {
          if (!data) return;
          setRoomNumber(data.room_number ? String(data.room_number) : roomId);
          const guestName = typeof data.guest_name === 'string' ? data.guest_name.trim() : '';
          const hasCheckedInGuest = Boolean(
            guestName ||
            data.guest_id ||
            data.guest_mobile ||
            data.guest_phone ||
            data.is_checked_in === true ||
            data.checked_in === true ||
            data.checked_in === 1
          );
          setCheckedInGuestName(guestName);
          setIsRoomOccupied(hasCheckedInGuest);
        })
        .catch(() => {
          setRoomNumber(roomId);
          setCheckedInGuestName('');
          setIsRoomOccupied(false);
        });
      return;
    }
    setRoomNumber('');
    setCheckedInGuestName('');
    setIsRoomOccupied(false);
  }, [hotelId, roomId]);

  // Visitors must not keep or enter a mobile number when room has no allocated guest
  useEffect(() => {
    if (isRoomOccupied === false) {
      setMobile('');
      setError('');
    }
  }, [isRoomOccupied]);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    if (!/^\d{10}$/.test(mobile)) {
      setError('Please enter a valid 10-digit mobile number');
      setShake(true);
      setIsLoading(false);
      return;
    }
    if (!hotelId || !roomId) {
      setError('Invalid hotel or room.');
      setShake(true);
      setIsLoading(false);
      return;
    }
    if (!isRoomOccupied) {
      setError('This room does not have any guest allocated currently.');
      setShake(true);
      setIsLoading(false);
      return;
    }
    setSent(true);
    try {
      const res = await fetch(API_ENDPOINTS.ROOM_GUEST_VALIDATE_MOBILE(hotelId, roomId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Mobile number not found for checked-in guest in this room.');
        setShake(true);
        setSent(false);
        setIsLoading(false);
        return;
      }
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setStep('verify');
        setResendCooldown(RESEND_WAIT_SECONDS);
        setSent(false);
        setIsLoading(false);
      }, 900);
    } catch (err) {
      setError('Network error. Please try again.');
      setShake(true);
      setSent(false);
      setIsLoading(false);
    }
  };

  const handleOtpChange = (idx: number, value: string) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    if (value && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otp[idx] === '' && idx > 0) {
        inputsRef.current[idx - 1]?.focus();
      }
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^\d]/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newOtp = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i += 1) {
      newOtp[i] = pasted[i];
    }
    setOtp(newOtp);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const otpValue = otp.join('');
    if (!hotelId || !roomId) {
      setError('Invalid hotel or room.');
      setShake(true);
      setIsLoading(false);
      return;
    }
    setSent(true);
    try {
      const res = await fetch(API_ENDPOINTS.ROOM_GUEST_VALIDATE_OTP(hotelId, roomId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp: otpValue }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Invalid OTP. Please try again.');
        setShake(true);
        setSent(false);
        setIsLoading(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      const guestId = data.guest_id ? String(data.guest_id) : '1';
      setSent(false);
      setIsLoading(false);
      onSuccess(mobile, guestId);
    } catch (err) {
      setError('Network error. Please try again.');
      setShake(true);
      setSent(false);
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (isLoading || resendCooldown > 0 || !hotelId || !roomId || !mobile) return;
    setError('');
    setIsLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.ROOM_GUEST_VALIDATE_MOBILE(hotelId, roomId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || 'Unable to resend OTP. Please try again.');
        setShake(true);
        setIsLoading(false);
        return;
      }
      setOtp(Array(OTP_LENGTH).fill(''));
      setResendCooldown(RESEND_WAIT_SECONDS);
      setIsLoading(false);
      setTimeout(() => inputsRef.current[0]?.focus(), 50);
    } catch (err) {
      setError('Network error. Please try again.');
      setShake(true);
      setIsLoading(false);
    }
  };

  const maskedMobile = mobile ? `+91 ${mobile.slice(0, 2)}******${mobile.slice(-2)}` : '';
  const isOtpComplete = otp.every((digit) => digit.length === 1);

  return (
    <div className="guest-login-container">
      <div className="login-background">
        <div className="gradient-overlay"></div>
        <div className="floating-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
        </div>
      </div>
      
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <div className="logo-icon">
              <i className="fas fa-hotel"></i>
            </div>
          </div>
          <h1 className="login-title">{hotelLoading ? 'Loading...' : hotelName || 'Hotel'}</h1>
          <p className="login-subtitle" style={{ marginBottom: '8px', fontWeight: 600 }}>
            {roomNumber ? `Room ${roomNumber}` : roomId ? `Room ${roomId}` : ''}
          </p>
        </div>

        <div className="login-content">
          {step === 'request' && (
            <form onSubmit={handleRequestOtp} className={`login-form ${shake ? 'shake' : ''}`}>
              <div className="modern-otp-panel otp-container">
                <div className="otp-badge">
                  <i className="fas fa-mobile-alt"></i>
                  Quick Access
                </div>
                <h2 className="otp-title">
                  {isRoomOccupied === false
                    ? ''
                    : checkedInGuestName
                      ? `Welcome, ${checkedInGuestName}`
                      : 'Welcome'}
                </h2>
                <p className="otp-subtitle">
                  {isRoomOccupied === false
                    ? ''
                    : 'Enter your registered number to receive a one-time password.'}
                </p>
                {isRoomOccupied === true && !checkedInGuestName && (
                  <p className="occupancy-info success">
                    <i className="fas fa-user-check"></i>
                    Room is occupied and eligible for guest login.
                  </p>
                )}
                {isRoomOccupied === false && (
                  <p className="occupancy-info warning">
                    <i className="fas fa-bed"></i>
                    This room does not have any guest allocated currently.
                  </p>
                )}

                {isRoomOccupied === true && (
                  <div className="form-group">
                    <div className="modern-input-shell">
                      <span className="country-code">+91</span>
                      <div className="input-wrapper">
                        <input
                          type="tel"
                          className="form-input modern-form-input"
                          id="mobile"
                          value={mobile}
                          onChange={e => {
                            const val = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                            setMobile(val);
                          }}
                          placeholder="Enter 10-digit mobile number"
                          required
                          pattern="\d{10}"
                          maxLength={10}
                          minLength={10}
                          disabled={isLoading}
                          autoComplete="tel"
                          inputMode="numeric"
                        />
                        <div className="input-icon">
                          <i className="fas fa-phone"></i>
                        </div>
                      </div>
                    </div>
                    <p className="field-helper">Use the same number used at hotel check-in.</p>
                  </div>
                )}
                {isRoomOccupied === null && hotelId && roomId && (
                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <div className="modern-input-shell room-status-loading" aria-busy="true">
                      <span className="country-code">+91</span>
                      <div className="input-wrapper">
                        <input
                          type="tel"
                          className="form-input modern-form-input"
                          readOnly
                          tabIndex={-1}
                          placeholder="Checking room…"
                          disabled
                          value=""
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
              </div>
              )}

              {isRoomOccupied === true && (
                <button 
                  type="submit" 
                  className={`submit-btn ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading || mobile.length !== 10}
                >
                  {isLoading ? (
                    <>
                      <div className="spinner"></div>
                      <span>Sending OTP...</span>
                    </>
                  ) : success ? (
                    <>
                      <i className="fas fa-check-circle"></i>
                      <span>OTP Sent!</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      <span>Send OTP</span>
                    </>
                  )}
                </button>
              )}
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerifyOtp} className={`otp-form ${shake ? 'shake' : ''}`}>
              <div className="otp-container modern-otp-panel">
                <div className="otp-badge">
                  <i className="fas fa-shield-alt"></i>
                  Secure Verification
                </div>
                <h2 className="otp-title">Enter OTP</h2>
                <p className="otp-subtitle">
                  We sent a 6-digit code to <span>{maskedMobile || mobile}</span>
                </p>
                <div className="otp-inputs" ref={otpContainerRef}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      className="otp-input"
                      value={digit}
                      ref={el => { inputsRef.current[idx] = el; }}
                      onChange={e => handleOtpChange(idx, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(idx, e)}
                      onPaste={handleOtpPaste}
                      autoFocus={idx === 0}
                      disabled={isLoading}
                      aria-label={`OTP digit ${idx + 1}`}
                    />
                  ))}
                </div>
                <p className="otp-hint">Didn't receive the code?</p>
                <div className="otp-actions">
                  <button 
                    type="button" 
                    className="resend-link"
                    onClick={handleResendOtp}
                    disabled={isLoading || resendCooldown > 0}
                  >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                  </button>
                  <button
                    type="button"
                    className="resend-link secondary"
                    onClick={() => {
                      setStep('request');
                      setOtp(Array(OTP_LENGTH).fill(''));
                      setError('');
                    }}
                    disabled={isLoading}
                  >
                    Change number
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {error}
              </div>
              )}

              <button 
                type="submit" 
                className={`submit-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading || !isOtpComplete}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-unlock"></i>
                    <span>Verify & Continue</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        <div className="login-footer">
          <p className="footer-text">
            Secure access powered by <span className="brand">QRoom</span>
          </p>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .guest-login-container {
          height: 100vh;
          width: 100vw;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }

        .login-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
        }

        .gradient-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
        }

        .floating-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .floating-circle {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 6s ease-in-out infinite;
        }

        .circle-1 {
          width: 100px;
          height: 100px;
          top: 20%;
          left: 10%;
          animation-delay: 0s;
        }

        .circle-2 {
          width: 150px;
          height: 150px;
          top: 60%;
          right: 15%;
          animation-delay: 2s;
        }

        .circle-3 {
          width: 80px;
          height: 80px;
          bottom: 20%;
          left: 20%;
          animation-delay: 4s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        .login-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: clamp(20px, 4vh, 40px);
          width: clamp(280px, 90vw, 400px);
          max-height: 90vh;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          z-index: 2;
          border: 1px solid rgba(255, 255, 255, 0.2);
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
        }

        .login-header {
          text-align: center;
          margin-bottom: clamp(16px, 3vh, 32px);
          flex-shrink: 0;
        }

        .logo-container {
          margin-bottom: clamp(8px, 2vh, 16px);
        }

        .logo-icon {
          width: clamp(40px, 8vh, 60px);
          height: clamp(40px, 8vh, 60px);
          background: linear-gradient(135deg, #4a6cf7, #667eea);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          font-size: clamp(18px, 4vh, 24px);
          color: white;
        }

        .login-title {
          font-size: clamp(20px, 5vh, 28px);
          font-weight: 700;
          color: #333;
          margin-bottom: clamp(4px, 1vh, 8px);
          line-height: 1.2;
        }

        .login-subtitle {
          font-size: clamp(14px, 3vh, 16px);
          color: #666;
          margin: 0;
          line-height: 1.4;
        }

        .login-content {
          margin-bottom: clamp(12px, 2vh, 24px);
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 0;
        }

        .login-form, .otp-form {
          display: flex;
          flex-direction: column;
          gap: clamp(12px, 2vh, 20px);
          height: 100%;
          justify-content: center;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: clamp(4px, 1vh, 8px);
        }

        .form-label {
          font-size: clamp(12px, 2.5vh, 14px);
          font-weight: 600;
          color: #333;
          display: flex;
          align-items: center;
          gap: clamp(4px, 1vh, 8px);
        }

        .form-label i {
          color: #4a6cf7;
        }

        .input-wrapper {
          position: relative;
        }

        .form-input {
          width: 100%;
          padding: clamp(12px, 2.5vh, 16px) clamp(12px, 2.5vh, 16px) clamp(12px, 2.5vh, 16px) clamp(36px, 4vh, 48px);
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: clamp(14px, 3vh, 16px);
          background: white;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .modern-login-panel {
          padding: clamp(14px, 2.2vh, 18px);
          border: 1px solid #e6e9f3;
          border-radius: 16px;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
        }

        .modern-input-shell {
          display: flex;
          align-items: center;
          border: 2px solid #dfe4f2;
          border-radius: 14px;
          background: #fff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .modern-input-shell:focus-within {
          border-color: #4a6cf7;
          box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
        }

        .country-code {
          padding: 0 12px;
          font-weight: 700;
          color: #1f2a44;
          border-right: 1px solid #e5eaf7;
          line-height: 1;
        }

        .modern-form-input {
          border: none;
          border-radius: 0 12px 12px 0;
          box-shadow: none !important;
          padding-left: 12px;
          padding-right: 44px;
          background: transparent;
        }

        .field-helper {
          margin: 0;
          font-size: 12px;
          color: #7a839f;
        }

        .occupancy-info {
          margin: 0;
          padding: 10px 12px;
          border-radius: 10px;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .occupancy-info.success {
          color: #0f5132;
          background: #e8f7ef;
          border: 1px solid #b7e5ca;
        }

        .occupancy-info.warning {
          color: #7a2e0b;
          background: #fff3e8;
          border: 1px solid #ffd8ba;
        }

        .room-status-loading {
          opacity: 0.75;
          pointer-events: none;
        }

        .form-input:focus {
          outline: none;
          border-color: #4a6cf7;
          box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
        }

        .input-icon {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: clamp(14px, 3vh, 18px);
        }

        .otp-container {
          display: flex;
          flex-direction: column;
          gap: clamp(12px, 2vh, 20px);
        }

        .modern-otp-panel {
          padding: clamp(14px, 2.2vh, 18px);
          border: 1px solid #e6e9f3;
          border-radius: 16px;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.8);
        }

        .otp-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #3f55d9;
          background: #edf1ff;
          border: 1px solid #d9e1ff;
          border-radius: 999px;
          padding: 6px 10px;
          width: fit-content;
          margin: 0 auto;
        }

        .otp-title {
          font-size: clamp(20px, 4vh, 24px);
          line-height: 1.2;
          color: #1f2a44;
          text-align: center;
          margin: 2px 0 0;
        }

        .otp-subtitle {
          font-size: clamp(13px, 2.5vh, 15px);
          color: #5f6780;
          text-align: center;
          margin: 0;
        }

        .otp-subtitle span {
          color: #1f2a44;
          font-weight: 600;
        }

        .otp-inputs {
          --otp-gap: clamp(6px, 1.6vw, 12px);
          width: min(100%, 420px);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: var(--otp-gap);
        }

        .otp-input {
          width: 100%;
          aspect-ratio: 1 / 1;
          min-height: 44px;
          border: 2px solid #d7ddef;
          border-radius: 12px;
          text-align: center;
          font-size: clamp(16px, 4.2vw, 20px);
          font-weight: 700;
          color: #1f2a44;
          background: white;
          transition: all 0.3s ease;
        }

        .otp-input:focus {
          outline: none;
          border-color: #4a6cf7;
          box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.1);
        }

        .otp-hint {
          font-size: clamp(12px, 2.5vh, 14px);
          color: #666;
          text-align: center;
          margin: 0;
        }

        .otp-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .resend-link {
          background: none;
          border: none;
          color: #4a6cf7;
          text-decoration: underline;
          cursor: pointer;
          font-size: clamp(12px, 2.5vh, 14px);
          margin-left: 4px;
        }

        .resend-link:hover {
          color: #3a5ce7;
        }

        .resend-link.secondary {
          color: #6b7280;
          text-decoration: none;
          border: 1px solid #d5d9e3;
          border-radius: 999px;
          padding: 4px 10px;
        }

        .resend-link.secondary:hover {
          color: #3f55d9;
          border-color: #3f55d9;
          background: #f5f7ff;
        }

        .error-message {
          padding: clamp(8px, 1.5vh, 12px) clamp(12px, 2vh, 16px);
          background: #fee;
          border: 1px solid #fcc;
          border-radius: 8px;
          color: #c33;
          font-size: clamp(12px, 2.5vh, 14px);
          display: flex;
          align-items: center;
          gap: clamp(4px, 1vh, 8px);
        }

        .submit-btn {
          padding: clamp(12px, 2.5vh, 16px);
          background: linear-gradient(135deg, #4a6cf7, #667eea);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: clamp(14px, 3vh, 16px);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: clamp(4px, 1vh, 8px);
          min-height: clamp(44px, 6vh, 52px);
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(74, 108, 247, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .submit-btn.loading {
          pointer-events: none;
        }

        .spinner {
          width: clamp(16px, 3vh, 20px);
          height: clamp(16px, 3vh, 20px);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .login-footer {
          text-align: center;
          margin-top: clamp(12px, 2vh, 24px);
          flex-shrink: 0;
        }

        .footer-text {
          font-size: clamp(10px, 2vh, 12px);
          color: #999;
          margin: 0;
        }

        .brand {
          font-weight: 600;
          color: #4a6cf7;
        }

        .shake {
          animation: shake 0.5s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        @media (max-width: 480px) {
          .login-card {
            padding: 24px;
            margin: 16px;
          }
          
          .otp-inputs {
            --otp-gap: clamp(6px, 2vw, 9px);
            width: min(100%, 340px);
          }
          
          .otp-input {
            min-height: 40px;
            border-radius: 10px;
            font-size: clamp(15px, 4.8vw, 18px);
          }
        }
        `
      }} />
    </div>
  );
};

export default GuestOtp; 